import React, { FunctionComponent, useState, useEffect, MouseEvent, useCallback } from 'react';
import { sortBy } from 'lodash';
import jsonpath from 'jsonpath';
import { Grid, Box, makeStyles, Button } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import PersonData from './Tabs/PersonData';
import PersonLicenses from './Tabs/PersonLicenses';
import { ConfiguredFields, Form, InspectionType, Order, TabList } from '../../store/modules/application/types';
import { Contact, Person } from '../../store/modules/person/types';
import Loader from '../../components/Loader';
import TabPanel from '../../components/TabPanel';
import PersonContact from './Tabs/PersonContacts';
import PageNotFound from '../../components/PageNotFound';
import { PERSON_TAB_LIST, EXPERIMENTAL, CUSTOM_TAB_IDS_PERSON_DETAILS } from '../../store/app.constants';
import Header from '../../components/PageHeader/Header';
import Title from '../../components/PageHeader/Title';
import DetailPageComponent from '../../components/DetailPageComponent';
import Appointment from './Tabs/Appointment';
import AddAppointmentDialog from './Tabs/Appointment/AddAppointmentDialog';
import Modal from '../../components/Modal';
import Accounting from './Tabs/Accounting';
import AddLicenseDialog from '../PersonDetailTabs/Tabs/PersonLicenses/AddLicenseDialog';
import PersonComment from './Tabs/PersonComment';
import { getPersonSimpleName } from '../../utils/utils';
import PersonFiles from './Tabs/Files';
import History from './Tabs/History';

type PersonDetailTabsProps = {
    match: { params: { id: string } };
    person: Person;
    editForm: Form;
    getPerson: Function;
    isLoading: boolean;
    getLicenseList: Function;
    data: any;
    personUpdateLoadingStatus: boolean;
    getPersonContacts: Function;
    setActiveTab: Function;
    getAppointmentList: Function;
    personContactList: Contact[];
    isPersonContactLoading: boolean;
    users: [];
    licenseListAppointment: [];
    getLicenseListAppointment: Function;
    addAppointment: Function;
    handleAddLicense: Function;
    addLicense: Function;
    licenseTypesList: [];
    getLicenseTypes: Function;
    getPersonLicensesList: Function;
    getUserList: Function;
    appointmentTypeList: any[];
    getAppointmentTypeList: Function;
    personCommentList: Function;
    featureFlags: string[];
    hasMoreLicense: boolean;
    hasMoreAppointment: boolean;
    hasMoreContacts: boolean;
    inspectionTypes: InspectionType[];
    hasMorePersonComment: boolean;
    getPersonAccounting: Function;
    hasMoreAccounting: boolean;
    getInspectionType: Function;
    hasMoreFiles: boolean;
    getPersonFileList: Function;
    appointmentCount: number;
    licenseCount: number;
    personContactsCount: number;
    addBulkAppointments: Function;
    getPersonTypes: Function;
    personLicenseColumnFields: ConfiguredFields;
    configuredTabs: TabList;
};

const PersonDetailTabs: FunctionComponent<PersonDetailTabsProps> = (props) => {
    const {
        getPerson,
        isLoading,
        person,
        editForm,
        getAppointmentList,
        getPersonContacts,
        setActiveTab,
        personContactList,
        isPersonContactLoading,
        addAppointment,
        licenseListAppointment,
        getLicenseListAppointment,
        getUserList,
        users,
        addLicense,
        licenseTypesList,
        getPersonLicensesList,
        getLicenseTypes,
        appointmentTypeList,
        getAppointmentTypeList,
        personCommentList,
        featureFlags,
        hasMoreLicense,
        hasMoreAppointment,
        hasMoreContacts,
        inspectionTypes,
        hasMorePersonComment,
        getPersonAccounting,
        hasMoreAccounting,
        getInspectionType,
        getPersonFileList,
        hasMoreFiles,
        appointmentCount,
        licenseCount,
        personContactsCount,
        addBulkAppointments,
        getPersonTypes,
        personLicenseColumnFields,
        configuredTabs,
    } = props;

    const [tabList, setTabList] = useState<TabList>();
    const [isAddAppointmentModalOpen, setIsAddAppointmentModalOpen] = useState(false);
    const [isAddLicenseModalOpen, setIsAddLicenseModalOpen] = useState(false);

    const [skipLicense, setSkipLicense] = useState(0);
    const [skipAppointment, setSkipAppointment] = useState<number>(0);
    const [skipContacts, setSkipContacts] = useState<number>(0);
    const [skipRecord, setSkipRecord] = useState(0);
    const [skipAccounting, setSkipAccounting] = useState(0);
    const counties = jsonpath.value(editForm, '$..entries[?(@.fieldMapping.targetPath=="$.address.county")].choices');
    const isCountyFieldMappingPresent = jsonpath.value(
        editForm,
        '$..entries[?(@.fieldMapping.targetPath=="$.address.county")]',
    );

    const recordLimit = 20;
    const licenseLimit = 20;
    const contactsLimit = 20;
    const accountingLimit = 20;
    const appointmentLimit = 20;

    const [contactSortOrder, setContactSortOrder] = useState<Order>('desc');
    const [contactSortColumn, setContactSortColumn] = useState<string>('name');
    const [licenseSortOrder, setLicenseSortOrder] = useState<Order>('desc');
    const [licenseSortColumn, setLicenseSortColumn] = useState<string>('issueDate');
    const [appointmentSortOrder, setAppointmentSortOrder] = useState<Order>('desc');
    const [appointmentSortColumn, setAppointmentSortColumn] = useState<string>('appointmentDateTime');
    const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
    const [skipFile, setSkipFile] = useState(0);
    const FileLimit = 20;

    const personId = props.match.params.id;
    const isExperimentalFeature = featureFlags.includes(EXPERIMENTAL);

    const licenseListAppointmentFormatted = sortBy(licenseListAppointment, 'licenseNumber')?.map(
        (data: { id: string; licenseNumber: string; type: string; additionalData: Record<string, unknown> }) => {
            return {
                id: data?.id,
                licenseNumber: data?.licenseNumber,
                type: data?.type,
                rideName: data?.additionalData?.rideName,
            };
        },
    );

    useEffect(() => {
        isExperimentalFeature && getAppointmentTypeList();
        isExperimentalFeature && getInspectionType();
    }, [getAppointmentTypeList, getInspectionType, isExperimentalFeature]);

    useEffect(() => {
        personCommentList(personId, { limit: recordLimit, skip: skipRecord });
    }, [personCommentList, personId, skipRecord]);

    useEffect(() => {
        getPersonLicensesList(personId, {
            limit: licenseLimit,
            skip: skipLicense,
            orderCol: licenseSortColumn,
            orderBy: licenseSortOrder,
        });
    }, [getPersonLicensesList, personId, skipLicense, licenseSortColumn, licenseSortOrder]);

    useEffect(() => {
        getPersonContacts(personId, { skip: skipContacts, limit: contactsLimit });
    }, [getPersonContacts, personId, skipContacts]);

    useEffect(() => {
        getPersonAccounting(personId, { limit: accountingLimit, skip: skipAccounting });
    }, [getPersonAccounting, personId, skipAccounting]);

    useEffect(() => {
        getPerson(personId);
        getLicenseTypes();
        getPersonTypes();
    }, [getPerson, personId, getLicenseTypes, getPersonTypes]);

    useEffect(() => {
        getAppointmentList(personId, {
            skip: skipAppointment,
            limit: appointmentLimit,
            orderCol: appointmentSortColumn,
            orderBy: appointmentSortOrder,
        });
    }, [personId, getAppointmentList, skipAppointment, appointmentSortColumn, appointmentSortOrder]);

    useEffect(() => {
        getLicenseListAppointment(personId);
        getUserList();
    }, [personId, getLicenseListAppointment, getUserList]);

    useEffect(() => {
        getPersonFileList(personId, { limit: FileLimit, skip: skipFile });
    }, [getPersonFileList, skipFile, personId]);

    const setTabListBasedOnFeatureFlag = useCallback(() => {
        if (!isExperimentalFeature) {
            setTabList(PERSON_TAB_LIST.filter((item) => !CUSTOM_TAB_IDS_PERSON_DETAILS.includes(item?.id)));
        } else {
            setTabList(PERSON_TAB_LIST);
        }
    }, [isExperimentalFeature]);

    useEffect(() => {
        if (!configuredTabs) {
            setTabListBasedOnFeatureFlag();
        } else {
            setTabList(configuredTabs);
        }
    }, [setTabListBasedOnFeatureFlag, configuredTabs]);

    const onTabSelect = (tabName: string) => {
        setActiveTab(tabName);
    };
    const handleAddAppointment = (e: MouseEvent<HTMLElement>) => {
        e.stopPropagation();
        setIsAddAppointmentModalOpen(true);
    };

    const classes = makeStyles(() => ({
        appointmentContainer: {
            display: 'flex',
            justifyContent: 'flex-end',
        },
        addAppointment: {
            color: '#0277BD',
            marginRight: '40px',
            fontWeight: 600,
            cursor: 'pointer',
            letterSpacing: '0.3px,',
            display: 'flex',
            alignItems: 'center',
        },
    }))();

    const handleAddLicense = (e: MouseEvent<HTMLElement>) => {
        e.stopPropagation();
        setIsAddLicenseModalOpen(true);
    };

    const handleUploadFile = (e: MouseEvent<HTMLElement>) => {
        e.stopPropagation();
        setIsUploadModalOpen(true);
    };

    const updateContactSortOrder = (sortOrder: Order) => {
        setContactSortOrder(sortOrder);
        setSkipContacts(0);
    };

    const updateContactSortBy = (sortBy: string) => {
        setContactSortColumn(sortBy);
        setSkipContacts(0);
    };

    const updateLicenseSortBy = (sortBy: string) => {
        setLicenseSortColumn(sortBy);
        setSkipLicense(0);
    };

    const updateLicenseSortOrder = (sortOrder: Order) => {
        setLicenseSortOrder(sortOrder);
        setSkipLicense(0);
    };

    const updateAppointmentSortBy = (sortBy: string) => {
        setAppointmentSortColumn(sortBy);
        setSkipAppointment(0);
    };

    const updateAppointmentSortOrder = (sortOrder: Order) => {
        setAppointmentSortOrder(sortOrder);
        setSkipAppointment(0);
    };

    const renderTabsPanel = (activeTab: number) => {
        const tab = tabList && tabList[activeTab];
        switch (tab?.id) {
            case 'personData':
                return (
                    <TabPanel activeTab={activeTab} index={activeTab}>
                        <PersonData person={person} editForm={editForm} />
                    </TabPanel>
                );
            case 'contacts':
                return (
                    <TabPanel activeTab={activeTab} index={activeTab}>
                        <PersonContact
                            person={person}
                            loading={isPersonContactLoading}
                            personContactList={personContactList}
                            hasMore={hasMoreContacts}
                            skip={skipContacts}
                            setSkip={setSkipContacts}
                            sortBy={contactSortColumn}
                            sortOrder={contactSortOrder}
                            setSortColumn={updateContactSortBy}
                            setSortOrder={updateContactSortOrder}
                            personContactsCount={personContactsCount}
                        />
                    </TabPanel>
                );
            case 'licenses':
                return (
                    <TabPanel activeTab={activeTab} index={activeTab}>
                        <PersonLicenses
                            isAddLicenseModalOpen={isAddLicenseModalOpen}
                            handleAddLicense={(e: MouseEvent<HTMLElement>) => {
                                handleAddLicense && handleAddLicense(e);
                            }}
                            skip={skipLicense}
                            setSkip={setSkipLicense}
                            hasMore={hasMoreLicense}
                            personId={person.id}
                            setSortColumn={updateLicenseSortBy}
                            setSortOrder={updateLicenseSortOrder}
                            sortBy={licenseSortColumn}
                            sortOrder={licenseSortOrder}
                            licenseCount={licenseCount}
                            personLicenseColumnFields={personLicenseColumnFields}
                        />
                    </TabPanel>
                );
            case 'accounting':
                return (
                    <TabPanel activeTab={activeTab} index={activeTab}>
                        <Accounting
                            person={person}
                            limit={accountingLimit}
                            skip={skipAccounting}
                            setSkip={setSkipAccounting}
                            hasMore={hasMoreAccounting}
                            getAccounting={getPersonAccounting}
                        />
                    </TabPanel>
                );
            case 'events':
                return (
                    <TabPanel activeTab={activeTab} index={activeTab}>
                        <div className="verticalFillContainer">
                            <div className={classes.appointmentContainer}>
                                <Button
                                    startIcon={<AddIcon />}
                                    className={classes.addAppointment}
                                    onClick={(e: MouseEvent<HTMLElement>) => handleAddAppointment(e)}
                                >
                                    ADD APPOINTMENT
                                </Button>
                            </div>
                            <div className="verticalFillContent">
                                <Appointment
                                    skip={skipAppointment}
                                    setSkip={setSkipAppointment}
                                    hasMore={hasMoreAppointment}
                                    setSortColumn={updateAppointmentSortBy}
                                    setSortOrder={updateAppointmentSortOrder}
                                    sortBy={appointmentSortColumn}
                                    sortOrder={appointmentSortOrder}
                                    appointmentCount={appointmentCount}
                                />
                            </div>
                        </div>
                    </TabPanel>
                );
            case 'comments':
                return (
                    <TabPanel activeTab={activeTab} index={activeTab}>
                        <PersonComment
                            person={person}
                            skip={skipRecord}
                            setSkip={setSkipRecord}
                            hasMore={hasMorePersonComment}
                        ></PersonComment>
                    </TabPanel>
                );
            case 'files':
                return (
                    <TabPanel activeTab={activeTab} index={activeTab}>
                        <PersonFiles
                            personId={personId}
                            isUploadModalOpen={isUploadModalOpen}
                            getFileList={getPersonFileList}
                            onCloseUploadModal={(isUploadModalOpen: boolean) => {
                                setIsUploadModalOpen && setIsUploadModalOpen(isUploadModalOpen);
                            }}
                            handleUploadFile={handleUploadFile}
                            skip={skipFile}
                            setSkip={setSkipFile}
                            hasMore={hasMoreFiles}
                        />
                    </TabPanel>
                );
            case 'history':
                return (
                    <TabPanel activeTab={activeTab} index={activeTab}>
                        <History personId={personId} />
                    </TabPanel>
                );
            default:
                return null;
        }
    };

    if (isLoading) return <Loader />;
    else if (!isLoading && !person.id) return <PageNotFound />;
    else
        return (
            <div className="verticalFillContainer">
                <Header>
                    <Grid item xs={12} sm={12} md={12} lg={12}>
                        <Title titleText={`${getPersonSimpleName(person.name)}`} />
                    </Grid>
                </Header>
                <Box width="100%" display="flex" className="detail detail-mobile verticalFillContent">
                    <DetailPageComponent
                        displayTabs={renderTabsPanel}
                        tabList={tabList}
                        onTabSelect={onTabSelect}
                    ></DetailPageComponent>
                </Box>
                {/* Modal for Add Appointment */}
                <Modal open={isAddAppointmentModalOpen} maxWidth={'md'} fullWidth={true} minHeight={'40vh'}>
                    <AddAppointmentDialog
                        setIsAddAppointmentModalOpen={setIsAddAppointmentModalOpen}
                        person={person}
                        addAppointment={addAppointment}
                        issuedByUsers={users}
                        licenseListAppointment={licenseListAppointmentFormatted}
                        appointmentTypes={appointmentTypeList}
                        inspectionTypes={inspectionTypes}
                        addBulkAppointments={addBulkAppointments}
                        counties={counties}
                        isCountyFieldMappingPresent={isCountyFieldMappingPresent}
                    />
                </Modal>

                {/* Modal for Add License */}
                <Modal open={isAddLicenseModalOpen} maxWidth={'sm'} fullWidth={true}>
                    <AddLicenseDialog
                        addLicense={addLicense}
                        setIsAddLicenseModalOpen={setIsAddLicenseModalOpen}
                        licenseTypesList={licenseTypesList}
                        person={person}
                    />
                </Modal>
            </div>
        );
};

export default PersonDetailTabs;
